/**
 * Seller intake (/sell) — submissions owned by the Seller Connect Engine
 * endpoints in resolve-api (/seller-connect).
 */
import { ObjectValues } from 'types';

export const SELLER_CONNECT_INTAKE_STATUS = {
  NOT_FINISHED: 'not_finished',
  COMPLETE: 'complete',
  INCOMPLETE: 'incomplete',
} as const;

export type SellerConnectIntakeStatus = ObjectValues<
  typeof SELLER_CONNECT_INTAKE_STATUS
>;

export type SellerConnectBusinessDetails = {
  businessName: string | null;
  entityName: string | null;
  tradingName: string | null;
  entityType: string | null;
  industryBucket: string | null;
  industryCode: string | null;
  industryLabel: string | null;
  registrationStatus: string | null;
  businessStructure: string | null;
  state: string | null;
  postcode: string | null;
  employeeCount: string | null;
  revenueRange: string | null;
  askingPrice: string | null;
  reasonForSelling: string | null;
  sellingTimeline: string | null;
};

export type SellerConnectSubmission = {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  intakeStatus: SellerConnectIntakeStatus;
  missingImDetails: string[];
  leadSource: string;
  matchStatus: SellerConnectMatchStatus;
  pathSelection: SellerConnectPathSelection | null;
  archivedAt: string | null;
  businessDetails: SellerConnectBusinessDetails;
  latestScreening: SellerConnectLatestScreening | null;
  latestTimeline: SellerConnectLatestTimeline | null;
  im: SellerConnectSubmissionIm | null;
  createdAt: string;
  updatedAt: string;
};

export type SellerConnectCreateSubmissionBody = {
  email: string;
  firstName: string;
  lastName?: string;
  phone?: string;
  businessName?: string;
  industryBucket?: SellerConnectIndustry;
  industryOther?: string;
  privacyConsent?: boolean;
  recordingConsent?: boolean;
  screeningMode?: SellerConnectScreeningMode;
  leadSource?: string;
  leadSourceRaw?: Record<string, string> | null;
};

export type SellerConnectSession = {
  token: string;
  refreshToken: string;
};

export type SellerConnectCreateSubmissionResponse = {
  submission: SellerConnectSubmission;
  submissionToken: string;
};

export const SELLER_CONNECT_INDUSTRY = {
  MANUFACTURING: 'manufacturing_wholesale_distribution',
  PROFESSIONAL_SERVICES: 'professional_services',
  HOSPITALITY: 'hospitality_retail_food',
  HEALTH: 'health_medical_wellness',
  PROPERTY: 'property_and_facilities',
  OTHER: 'other',
} as const;

export type SellerConnectIndustry = ObjectValues<
  typeof SELLER_CONNECT_INDUSTRY
>;

export const SELLER_CONNECT_REVENUE_RANGE = {
  UP_TO_250K: '$0-250k',
  FROM_250K_TO_500K: '$250k-500k',
  FROM_500K_TO_1M: '$500k-1M',
  FROM_1M_TO_2M: '$1M-2M',
  FROM_2M_TO_5M: '$2M-5M',
  OVER_5M: '$5M+',
} as const;

export type SellerConnectRevenueRange = ObjectValues<
  typeof SELLER_CONNECT_REVENUE_RANGE
>;

export const SELLER_CONNECT_SELLING_TIMELINE = {
  IMMEDIATE: 'immediate',
  ONE_TO_THREE_MONTHS: '1-3_months',
  THREE_TO_SIX_MONTHS: '3-6_months',
  SIX_TO_TWELVE_MONTHS: '6-12_months',
  EXPLORING: 'exploring',
} as const;

export type SellerConnectSellingTimeline = ObjectValues<
  typeof SELLER_CONNECT_SELLING_TIMELINE
>;

export const SELLER_CONNECT_PATH_SELECTION = {
  OFF_MARKET: 'off_market',
  BROKER_CONNECT: 'broker_connect',
} as const;

export type SellerConnectPathSelection = ObjectValues<
  typeof SELLER_CONNECT_PATH_SELECTION
>;

export const SELLER_CONNECT_SCREENING_MODE = {
  TEXT: 'text',
  VOICE: 'voice',
  CHAT: 'chat',
} as const;

export type SellerConnectScreeningMode = ObjectValues<
  typeof SELLER_CONNECT_SCREENING_MODE
>;

export const SELLER_CONNECT_SCREENING_STATUS = {
  IN_PROGRESS: 'in_progress',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ABANDONED: 'abandoned',
} as const;

export type SellerConnectScreeningStatus = ObjectValues<
  typeof SELLER_CONNECT_SCREENING_STATUS
>;

export const SELLER_CONNECT_SCREENING_BLOCK = {
  BUSINESS_OVERVIEW: 'business_overview',
  CUSTOMERS_AND_FINANCIALS: 'customers_and_financials',
  SALE_READINESS: 'sale_readiness',
  STRENGTHS_AND_RISKS: 'strengths_and_risks',
} as const;

export type SellerConnectScreeningBlock = ObjectValues<
  typeof SELLER_CONNECT_SCREENING_BLOCK
>;

export type SellerConnectScreeningResponse = {
  block: SellerConnectScreeningBlock;
  questionKey: string;
  questionText: string;
  responseText: string;
  source: SellerConnectAnswerSource;
};

export type SellerConnectScreeningResponseInput = Pick<
  SellerConnectScreeningResponse,
  'questionKey' | 'responseText'
>;

export type SellerConnectScreeningSession = {
  _id: string;
  submissionId: string;
  mode: SellerConnectScreeningMode;
  status: SellerConnectScreeningStatus;
  exceptionReason: SellerConnectScreeningExceptionReason | null;
  missingImDetails: string[];
  responses: SellerConnectScreeningResponse[];
  messages?: SellerConnectChatMessage[];
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
};

export const SELLER_CONNECT_SCREENING_EXCEPTION_REASON = {
  SWITCH_TO_TEXT: 'switch_to_text',
  DISTRESS_DETECTED: 'distress_detected',
} as const;

export type SellerConnectScreeningExceptionReason = ObjectValues<
  typeof SELLER_CONNECT_SCREENING_EXCEPTION_REASON
>;

export type SellerConnectRuntimeConfig = {
  voiceScreeningEnabled: boolean;
  chatEnabled: boolean;
  formEnabled: boolean;
  industries: SellerConnectOption[];
  questions: SellerConnectQuestionSchema;
};

export type SellerConnectVoiceCall = {
  session_id: string;
  call_id: string;
  access_token: string;
};

export const SELLER_CONNECT_MATCH_STATUS = {
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  MATCHING: 'matching',
  BROKERS_REVIEWING: 'brokers_reviewing',
  BROKER_MATCHED: 'broker_matched',
  IN_CONVERSATION: 'in_conversation',
  MANDATE_SIGNED: 'mandate_signed',
  CONCLUDED: 'concluded',
  EXPIRED: 'expired',
} as const;

export type SellerConnectMatchStatus = ObjectValues<
  typeof SELLER_CONNECT_MATCH_STATUS
>;

export const SELLER_CONNECT_TIMELINE_STATUS = {
  SCREENING_COMPLETE: 'screening_complete',
  MATCHING_IN_PROGRESS: 'matching_in_progress',
  BROKERS_REVIEWING: 'brokers_reviewing',
  BROKER_MATCHED: 'broker_matched',
  NO_MATCH_FOUND: 'no_match_found',
} as const;

export type SellerConnectTimelineStatus = ObjectValues<
  typeof SELLER_CONNECT_TIMELINE_STATUS
>;

export type SellerConnectLatestScreening = {
  _id: string;
  mode: SellerConnectScreeningMode;
  status: SellerConnectScreeningStatus;
  completedAt: string | null;
};

export type SellerConnectLatestTimeline = {
  status: SellerConnectTimelineStatus;
  createdAt: string;
  advisorDisplayName: string | null;
};

export type SellerConnectMagicLinkSession = SellerConnectSession & {
  redirectPath: string;
};

export const SELLER_CONNECT_CHAT_ROLE = {
  AGENT: 'agent',
  USER: 'user',
} as const;

export type SellerConnectChatRole = ObjectValues<
  typeof SELLER_CONNECT_CHAT_ROLE
>;

export type SellerConnectChatMessage = {
  role: SellerConnectChatRole;
  content: string;
  createdAt?: string;
};

export type SellerConnectChatReply = {
  messages: SellerConnectChatMessage[];
  ended: boolean;
};

export const SELLER_CONNECT_ANSWER_SOURCE = {
  FORM: 'form',
  VOICE: 'voice',
  CHAT: 'chat',
} as const;

export type SellerConnectAnswerSource = ObjectValues<
  typeof SELLER_CONNECT_ANSWER_SOURCE
>;

export const SELLER_CONNECT_QUESTION_TYPE = {
  TEXT: 'text',
  SELECTOR: 'selector',
  NUMBER: 'number',
  AMOUNT: 'amount',
  SHORT_TEXT: 'short_text',
} as const;

export type SellerConnectQuestionType = ObjectValues<
  typeof SELLER_CONNECT_QUESTION_TYPE
>;

export const SELLER_CONNECT_QUESTION_REQUIREMENT = {
  IM_DETAIL: 'im_detail',
  OPTIONAL: 'optional',
  INTERNAL: 'internal',
} as const;

export type SellerConnectQuestionRequirement = ObjectValues<
  typeof SELLER_CONNECT_QUESTION_REQUIREMENT
>;

export type SellerConnectOption = {
  value: string;
  label: string;
};

export type SellerConnectQuestion = {
  key: string;
  label: string;
  question: string;
  type: SellerConnectQuestionType;
  requirement: SellerConnectQuestionRequirement;
  options?: SellerConnectOption[];
  min?: number;
  max?: number;
  acceptsNotSure?: true;
  showWhen?: SellerConnectQuestionCondition;
  maxLength?: number;
};

export type SellerConnectQuestionBlock = {
  id: SellerConnectScreeningBlock;
  label: string;
  questions: SellerConnectQuestion[];
};

export type SellerConnectQuestionSchema = {
  blocks: SellerConnectQuestionBlock[];
  textMaxLength: number;
};

export type SellerConnectUpdateSubmissionBody = {
  firstName: string;
  lastName?: string;
  phone?: string;
};

export type SellerConnectQuestionCondition = {
  key: string;
  in?: string[];
  notIn?: string[];
};

export const SELLER_CONNECT_SUBMISSION_IM_STATUS = {
  PREPARING: 'preparing',
  READY: 'ready',
} as const;

export type SellerConnectSubmissionImStatus = ObjectValues<
  typeof SELLER_CONNECT_SUBMISSION_IM_STATUS
>;

export type SellerConnectSubmissionIm = {
  status: SellerConnectSubmissionImStatus;
  sentAt: string | null;
};

export type SellerConnectReturnTokenRedeem = {
  exists: boolean;
  submissionId?: string;
  status?: SellerConnectIntakeStatus;
  firstName?: string;
  lastName?: string | null;
  email?: string;
  submissionToken?: string;
};

export const SELLER_CONNECT_IM_VIEW_STATUS = {
  NOT_STARTED: 'not_started',
  PREPARING: 'preparing',
  READY: 'ready',
} as const;

export type SellerConnectImViewStatus = ObjectValues<
  typeof SELLER_CONNECT_IM_VIEW_STATUS
>;

export const SELLER_CONNECT_IM_SECTION_TYPE = {
  PARAGRAPHS: 'paragraphs',
  BULLETS: 'bullets',
  FACTS: 'facts',
} as const;

export type SellerConnectImFact = {
  key?: string;
  label: string;
  value: string;
  missing?: boolean;
};

export type SellerConnectImSection =
  | {
      id: string;
      title: string;
      type: typeof SELLER_CONNECT_IM_SECTION_TYPE.PARAGRAPHS;
      paragraphs: string[];
    }
  | {
      id: string;
      title: string;
      type: typeof SELLER_CONNECT_IM_SECTION_TYPE.BULLETS;
      tone: 'positive' | 'growth' | 'risk';
      bullets: string[];
    }
  | {
      id: string;
      title: string;
      type: typeof SELLER_CONNECT_IM_SECTION_TYPE.FACTS;
      facts: SellerConnectImFact[];
    };

export type SellerConnectImContent = {
  title: string;
  subtitle: string;
  location?: string;
  industry?: string;
  highlights: string[];
  draftNote: string;
  sections: SellerConnectImSection[];
  notIncluded: string;
};

export type SellerConnectIm = {
  status: SellerConnectImViewStatus;
  version?: number;
  sentAt?: string | null;
  pageCount?: number | null;
  content?: SellerConnectImContent;
  isUpdating?: boolean;
  textOutdated?: boolean;
  editedSectionIds?: string[];
  editedDetailKeys?: string[];
  answers?: Record<string, string>;
};

export type SellerConnectMagicLinkResend = {
  sent: boolean;
  email: string;
};

export const SELLER_CONNECT_SELLER_FILTER_OPTION_FIELD = {
  NAME: 'name',
  BUSINESS: 'business',
  INDUSTRY: 'industry',
} as const;

export type SellerConnectSellerFilterOptionField = ObjectValues<
  typeof SELLER_CONNECT_SELLER_FILTER_OPTION_FIELD
>;

export type SellerConnectSeller = {
  _id: string;
  sceSellerId: string;
  submittedAt: string | null;
  seller: SellerConnectSellerContact;
  business: SellerConnectSellerBusiness;
  screening: {
    mode: SellerConnectScreeningMode;
  } | null;
  intakeStatus?: SellerConnectIntakeStatus;
};

export type SellerConnectSellersFilters = {
  name?: string | string[];
  business?: string | string[];
  industry?: string | string[];
  state?: string | string[];
  revenueRange?: string | string[];
  mode?: string | string[];
  intakeStatus?: string | string[];
  submittedTimeframe?: string;
};

export type SellerConnectAdminAnswer = {
  key: string;
  label: string;
  requirement: SellerConnectQuestionRequirement;
  display: string | null;
};

export type SellerConnectAdminSubmission = {
  _id: string;
  createdAt: string;
  submittedAt: string | null;
  intakeStatus: SellerConnectIntakeStatus;
  seller: SellerConnectSellerContact;
  business: {
    name: string;
  };
  screening: SellerConnectAdminScreening | null;
  imVersions: SellerConnectAdminImVersion[];
};

export type SellerConnectSellerContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
};

export type SellerConnectSellerBusiness = {
  businessName: string | null;
  entityName: string | null;
  tradingName: string | null;
  industryLabel: string | null;
  state: string | null;
  postcode: string | null;
  revenueRange: SellerConnectRevenueRange | null;
};

export type SellerConnectAdminScreening = {
  mode: SellerConnectScreeningMode;
  startedAt: string | null;
  completedAt: string | null;
  blocks: SellerConnectAdminScreeningBlock[];
  transcript: string | SellerConnectChatMessage[] | null;
  recording: SellerConnectAdminScreeningRecording | null;
};

export type SellerConnectAdminScreeningBlock = {
  id: string;
  label: string;
  answers: SellerConnectAdminAnswer[];
};

export type SellerConnectAdminScreeningRecording = {
  url: string;
  durationSeconds: number | null;
};

export const SELLER_CONNECT_IM_STATUS = {
  GENERATING: 'generating',
  READY: 'ready',
  SENT: 'sent',
  FAILED: 'failed',
} as const;

export type SellerConnectImStatus = ObjectValues<
  typeof SELLER_CONNECT_IM_STATUS
>;

export type SellerConnectAdminImVersion = {
  version: number;
  status: SellerConnectImStatus;
  createdAt: string;
  sentAt: string | null;
};
